import { useState, useEffect } from 'react';
import './LandingPage.css';
import './BookingPage.css';
import { useLang, LangSwitch, tr } from './lib/i18n';
import { findBooking, BookingNotFoundError, BookingLookupError } from './lib/api';
import { buildLegs, buildPassengers, buildPrice, formatMoney, buildBaggage, buildConditions, baggageStatusLabel, conditionStatusLabel } from './lib/tripFormat';
import QRCode from 'qrcode';
import { downloadTicketPdf, bookingLookupUrl } from './lib/pdfTicket';

const IMG = process.env.PUBLIC_URL + '/assets/AfriTrip Landing Page_img';
const LOGO_COLOR = `${IMG}/LOGO-FLYAFRITRIP 1-2.png`;

function Nav() {
  useLang();
  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <a href={process.env.PUBLIC_URL || "/"}><img src={LOGO_COLOR} alt="FlyAfritrip" className="nav-logo" /></a>
        <div className="nav-links">
          <a href="/#features">{tr('navFeatures')}</a>
          <a href="/#security">{tr('navSecurity')}</a>
          <a href="/#contact">{tr('navContact')}</a>
        </div>
        <LangSwitch />
        <a href="/#download" className="btn-nav">{tr('navDownload')}</a>
      </div>
    </nav>
  );
}

function Footer() {
  useLang();
  return (
    <footer className="footer">
      <div className="footer-inner">
        <img src={LOGO_COLOR} alt="FlyAfritrip" className="footer-logo" />
        <span className="footer-copy">{tr('footerCopy')}</span>
        <div className="footer-links">
          <a href="/#terms">{tr('footerTerms')}</a>
          <span>|</span>
          <a href="/#privacy">{tr('footerPrivacy')}</a>
          <span>|</span>
          <a href="/#contact">{tr('footerContact')}</a>
        </div>
      </div>
    </footer>
  );
}

// "310353161" -> "310-353-161". Anything that isn't a plain 9-digit booking
// number (PNRs, FAT references) is left exactly as typed.
function formatReference(raw) {
  const compact = raw.replace(/[\s-]/g, '');
  return /^\d{9}$/.test(compact) ? `${compact.slice(0, 3)}-${compact.slice(3, 6)}-${compact.slice(6)}` : raw;
}

function TripResult({ tripDetails, transactionSummary, fareRules }) {
  useLang();
  const booking = tripDetails.bookings?.[0] ?? {};
  const legs = buildLegs(booking, tripDetails.carriers, tripDetails.airports);
  const passengers = buildPassengers(booking);
  const baggage = buildBaggage(booking, fareRules);
  const conditions = buildConditions(fareRules);
  const { total, currency, seatFees } = buildPrice(booking, transactionSummary);
  const [downloading, setDownloading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const lookupUrl = bookingLookupUrl(tripDetails.reference_number, tripDetails.primary_contact?.last_name);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(lookupUrl, { margin: 0, color: { dark: '#1D2939', light: '#0000' } })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [lookupUrl]);
  // Three real stages exist (payment confirmed -> booking confirmed with a
  // PNR -> ticket issued later), not two. FlightHub's own status field is
  // "issued" only once the ticket itself is actually cut.
  const ticketIssued = tripDetails.status === 'issued';
  const cancelled = /^cancel+ed$/i.test(tripDetails.status || '');

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadTicketPdf({ tripDetails, transactionSummary, fareRules });
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="trip-result">
      {/* Same boarding-pass look as the PDF ticket (src/lib/pdfTicket.js) —
          orange accent bar, E-TICKET + status badge, one card per leg with
          a perforated "stub" and a decorative barcode — so the on-screen
          view and the downloaded PDF read as the same document. */}
      <div className="ticket-accent-bar" />
      <div className="ticket-header">
        <div>
          <p className="trip-eyebrow">{tr('bookingRef')}</p>
          <h2 className="trip-ref">{tripDetails.reference_number}</h2>
        </div>
        <div className="ticket-header-right">
          {/* Booking confirmed and ticket issued are separate stages; only claim "ticket issued" once it is. */}
          <span className="ticket-eticket-label">{cancelled ? tr('labelCancelled') : ticketIssued ? tr('labelIssued') : tr('labelConfirmed')}</span>
          <span className={'trip-status-badge' + (cancelled ? ' trip-status-badge--cancelled' : ticketIssued ? '' : ' trip-status-badge--pending')}>
            {cancelled ? tr('badgeCancelled') : ticketIssued ? tr('badgeIssued') : tr('badgeConfirmed')}
          </span>
        </div>
      </div>
      {cancelled && (
        <p className="trip-ticket-pending-note">
          {tr('cancelledNote')}
        </p>
      )}

      {legs.map((leg, i) => (
        <div className="ticket-leg-card" key={i}>
          <div className="ticket-leg-strip">
            <span className="ticket-leg-label">{leg.label.toUpperCase()}</span>
            <span className="ticket-leg-date">{leg.dateLabel}</span>
          </div>

          <div className="ticket-leg-body">
            <div className="trip-leg-route">
              <div className="trip-leg-point">
                <strong>{leg.from}</strong>
                {leg.fromPlace && <span className="trip-leg-place">{leg.fromPlace}</span>}
                <span>{leg.departureTime}</span>
              </div>
              <div className="trip-leg-line">
                <span>{leg.duration}</span>
                <div className="trip-leg-dash" />
                <span>{leg.stopsLabel}</span>
              </div>
              <div className="trip-leg-point">
                <strong>{leg.to}</strong>
                {leg.toPlace && <span className="trip-leg-place">{leg.toPlace}</span>}
                <span>{leg.arrivalTime}</span>
              </div>
            </div>
            <div className="trip-leg-meta">
              <strong>{leg.airline}</strong>
              <span>{tr('cabinLine')} {leg.cabin}</span>
            </div>
          </div>

          <div className="ticket-perforation">
            <span className="ticket-notch ticket-notch-left" />
            <span className="ticket-notch ticket-notch-right" />
          </div>

          <div className="ticket-stub">
            <div className="ticket-stub-field">
              <span>{tr('stubRef')}</span>
              <strong>{tripDetails.reference_number}</strong>
            </div>
            {leg.pnr && (
              <div className="ticket-stub-field">
                <span>{tr('stubPnr')}</span>
                <strong>{leg.pnr}</strong>
              </div>
            )}
            <div className="ticket-stub-field">
              <span>{tr('stubClass')}</span>
              <strong>{leg.cabin}</strong>
            </div>
            {leg.seatsText && (
              <div className="ticket-stub-field">
                <span>{tr('stubSeat')}</span>
                <strong>{leg.seatsText}</strong>
              </div>
            )}
            <div className="ticket-barcode-wrap">
              {qrDataUrl ? <img className="ticket-qr" src={qrDataUrl} alt="" /> : <div className="ticket-qr" />}
              <span>{tr('scan')}</span>
            </div>
          </div>
        </div>
      ))}

      <div className="trip-card">
        <p className="trip-card-title">{tr('passengers')}</p>
        <div className={'trip-pax-table' + (ticketIssued ? '' : ' trip-pax-table--name-only')}>
          <div className="trip-pax-row trip-pax-head">
            <span>{tr('colName')}</span>{ticketIssued && <span>{tr('colEticket')}</span>}
          </div>
          {passengers.map((p, i) => (
            <div className="trip-pax-row" key={i}>
              <span>{p.name}</span>{ticketIssued && <span>{p.eTicket}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="trip-card">
        <p className="trip-card-title">{tr('bagTitle')}</p>
        {baggage.any ? (
          <div className="trip-bag-scroll">
            <table className="trip-bag-table">
              <thead>
                <tr>
                  <th>{tr('bagLeg')}</th>
                  <th>{tr('bagPersonal')}</th>
                  <th>{tr('bagCabin')}</th>
                  <th>{tr('bagChecked')}</th>
                  <th>{tr('bagChecked2')}</th>
                </tr>
              </thead>
              <tbody>
                {baggage.rows.map((r, i) => (
                  <tr key={i}>
                    <td data-label={tr('bagLeg')}><strong>{r.route}</strong></td>
                    <td data-label={tr('bagPersonal')} className={r.personal ? 'bag-' + r.personal : ''}>{baggageStatusLabel(r.personal) || '-'}</td>
                    <td data-label={tr('bagCabin')} className={r.cabin ? 'bag-' + r.cabin : ''}>{baggageStatusLabel(r.cabin) || '-'}</td>
                    <td data-label={tr('bagChecked')} className={r.checked ? 'bag-' + r.checked : ''}>
                      {baggageStatusLabel(r.checked) || r.allowance || '-'}
                      {r.allowance && r.checked ? <small>{r.allowance}</small> : null}
                    </td>
                    <td data-label={tr('bagChecked2')} className={r.checked2 ? 'bag-' + r.checked2 : ''}>{baggageStatusLabel(r.checked2) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="trip-bag-empty">{tr('bagUnavailable')}</p>
        )}
        {baggage.any && <p className="trip-bag-note">{tr('bagNote')}</p>}
      </div>

      {conditions.length > 0 && (
        <div className="trip-card">
          <p className="trip-card-title">{tr('bagFareTitle')}</p>
          <div className="trip-pax-table">
            {conditions.map((c, i) => (
              <div className="trip-cond-row" key={i}>
                <span>{c.label}</span>
                <strong className={'bag-' + c.status}>{conditionStatusLabel(c.status)}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="trip-total-card">
        <span>{tr('totalPaid')}</span>
        <strong>{formatMoney(total, currency)}</strong>
        {seatFees > 0 && (
          <small className="trip-total-seats">{tr('seatsFee')} {formatMoney(seatFees, currency)}</small>
        )}
      </div>

      <button className="btn-nav trip-download-btn" onClick={handleDownload} disabled={downloading}>
        {downloading ? tr('preparing') : cancelled ? tr('dlCancelled') : ticketIssued ? tr('dlIssued') : tr('dlConfirmed')}
      </button>
    </div>
  );
}

export default function BookingPage() {
  const lang = useLang();
  const [reference, setReference] = useState('');
  const [lastName, setLastName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | error | done
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    const ref = result?.tripDetails?.reference_number;
    document.title = ref ? tr('docTitleBooking', ref) : tr('docTitleFind');
  }, [result, lang]);

  async function runSearch(ref, last) {
    setStatus('loading');
    setErrorMessage('');
    try {
      const found = await findBooking(formatReference(ref), last);
      setResult(found);
      setStatus('done');
    } catch (err) {
      setResult(null);
      setStatus('error');
      if (err instanceof BookingNotFoundError) {
        setErrorMessage(tr('errNotFound'));
      } else if (err instanceof BookingLookupError) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage(tr('errGeneric'));
      }
    }
  }

  // The PDF e-ticket's QR code links here as /booking?ref=...&last=... so
  // scanning it actually retrieves the ticket instead of just opening a
  // blank form — prefill from the URL and search immediately on load.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref')?.trim();
    const last = params.get('last')?.trim();
    if (!ref || !last) return;
    setReference(ref);
    setLastName(last);
    runSearch(ref, last);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!reference.trim() || !lastName.trim()) return;
    runSearch(reference.trim(), lastName.trim());
  }

  return (
    <div className="app booking-page-shell">
      {/* Blurred so the mockup's own on-screen text (a screenshot of this
          same site) doesn't read as ghosted duplicate text competing with
          the real page content — a white overlay alone wasn't enough to
          fully hide it. Blur lives on this separate fixed layer, not the
          shell itself, so the actual content on top stays perfectly sharp. */}
      <div className="booking-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/booking/hero-bg.jpg)` }} />
      <Nav />
      <main className="booking-main">
        <section className="booking-hero">
          <div className="container container--narrow">
            <h1 className="booking-title">{tr('findTitle')}</h1>
            <p className="booking-sub">{tr('findSub')}</p>

            <form className="booking-form" onSubmit={handleSubmit}>
              <div className="booking-field">
                <label htmlFor="reference">{tr('refLabel')}</label>
                <input
                  id="reference"
                  type="text"
                  placeholder={tr('refPlaceholder')}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  required
                />
              </div>
              <div className="booking-field">
                <label htmlFor="lastName">{tr('lastLabel')}</label>
                <input
                  id="lastName"
                  type="text"
                  placeholder={tr('lastPlaceholder')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn-nav booking-submit" disabled={status === 'loading'}>
                {status === 'loading' ? tr('searching') : tr('findBtn')}
              </button>
            </form>

            {status === 'error' && <p className="booking-error">{errorMessage}</p>}
          </div>
        </section>

        {status === 'done' && result && (
          <section className="container container--narrow">
            <TripResult tripDetails={result.tripDetails} transactionSummary={result.transactionSummary} fareRules={result.fareRules} />
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
